import React, { createContext, useCallback, useEffect, useReducer } from 'react';
import { AuthenticationResult } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';
import Loader from 'components/Loader';
import { AuthContextType, AuthProps, UserProfile } from '../types/auth';
import { jwtDecode } from 'jwt-decode';
import { removeAxiosInterceptors, setUpAxiosInterceptors } from 'utils/axios';
import { BrowserCache } from 'utils/browserCache';

const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: undefined
};

const AzureB2CContext = createContext<AuthContextType | null>(null);

export const AzureB2CProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { instance: msalInstance } = useMsal();

  const getActiveAccount = useCallback(() => {
    let account = msalInstance.getActiveAccount();

    if (!account) {
      const accounts = msalInstance.getAllAccounts();

      console.log('Accounts found in cache:', accounts.length);

      if (accounts.length > 0) {
        account = accounts[0];
        msalInstance.setActiveAccount(account);
      }
    }

    if (!account) {
      throw new Error('No active account found. User is not logged in.');
    }

    return account;
  }, [msalInstance]);

  const acquireUserProfile = useCallback(async (): Promise<UserProfile> => {
    try {
      const account = getActiveAccount();

      // Fetch ID token
      const authResponse: AuthenticationResult = await msalInstance.acquireTokenSilent({
        scopes: [],
        account
      });

      // Decode ID token for user info and permissions
      const decodedToken: any = jwtDecode(authResponse.idToken);

      return {
        id: account.localAccountId,
        email: account.username,
        name: account.name,
        userId: decodedToken.extension_UserId || ''
      };
    } catch (error) {
      console.error('Acquire ID token failed:', error);
      throw new Error('User is not authenticated. Please log in again.');
    }
  }, [getActiveAccount, msalInstance]);

  const acquireAccessToken = useCallback(
    async (scope: string) => {
      try {
        let account = getActiveAccount();

        const authResponse: AuthenticationResult = await msalInstance.acquireTokenSilent({
          scopes: [scope],
          account
        });

        console.log('Tokens acquired successfully:', authResponse);

        if (!authResponse.accessToken) {
          throw new Error('No access token acquired.');
        }

        return authResponse.accessToken;
      } catch (error) {
        console.error('Silent login failed:', error);
        throw new Error('User is not authenticated. Please log in again.');
      }
    },
    [getActiveAccount, msalInstance]
  );

  const logout = useCallback(async () => {
    try {
      console.log('Starting logout process...');

      BrowserCache.clearAll();

      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin
      });
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, [msalInstance]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const user: UserProfile = await acquireUserProfile();

        dispatch({
          type: LOGIN,
          payload: {
            user: user
          }
        });
      } catch (error) {
        console.error('Error during authentication initialization:', error);
        dispatch({ type: LOGOUT });
        await logout();
      }
    };
    initAuth();
  }, [acquireUserProfile, logout, msalInstance]);

  useEffect(() => {
    const interceptors = setUpAxiosInterceptors(acquireAccessToken, logout);

    return () => removeAxiosInterceptors(interceptors);
  }, [acquireAccessToken, logout]);

  if (!state.isInitialized) {
    return <Loader />;
  }

  return (
    <AzureB2CContext.Provider
      value={{
        ...state,
        logout
      }}
    >
      {children}
    </AzureB2CContext.Provider>
  );
};

export default AzureB2CContext;
